import { Flex, Spin, Button } from 'antd';
import type { FC, ReactNode, CSSProperties } from 'react';
import { ScrollContainer } from '@shared/ui/scroll-container';
import styles from './dropdown-list.module.scss';
import { useTranslation } from 'react-i18next';

export interface DropdownListProps<T> {
  isOpen: boolean;
  items?: T[] | null;
  isLoading?: boolean;
  onFetch?: () => void;
  renderItem: (item: T, index: number) => ReactNode;
  emptyPlaceholder?: ReactNode;
  maxHeight?: string;
  style?: CSSProperties;
  className?: string;
  onCreate?: () => void;
  createLabel?: string;
  createIcon?: ReactNode;
  createClassName?: string;
}

export const DropdownList = <T,>({
  isOpen,
  items,
  isLoading,
  onFetch,
  renderItem,
  emptyPlaceholder,
  maxHeight = '200px',
  style,
  className,
  onCreate,
  createLabel,
  createIcon,
  createClassName,
}: DropdownListProps<T>) => {
  const { t } = useTranslation('translation');
  if (!isOpen) return null;

  return (
    <Flex vertical gap={8} className={className} style={style}>
      <div>
        <ScrollContainer height="auto" maxHeight={maxHeight} marginRight="0">
          {isLoading ? (
            <Flex align="center" justify="center" style={{ height: '100%', padding: '20px 0' }}>
              <Spin />
            </Flex>
          ) : items?.length ? (
            <Flex vertical gap={4} className={styles.list}>
              {items.map((item, index) => (
                <div key={index} className={styles.item}>
                  {renderItem(item, index)}
                </div>
              ))}
            </Flex>
          ) : (
            (emptyPlaceholder ?? <></>)
          )}
        </ScrollContainer>

        {onCreate && (
          <Button
            block
            icon={createIcon}
            iconPosition="end"
            onClick={onCreate}
            className={createClassName}
          >
            {createLabel ?? t('common.actions.create')}
          </Button>
        )}
      </div>
    </Flex>
  );
};
